import { runAz } from "./az.js";

export interface ModelQuota { name: string; currentValue: number; limit: number }

/** List Cognitive Services usages (quota) for a region. Returns [] on any az error. */
export async function listModelQuota(region: string): Promise<ModelQuota[]> {
  try {
    const out = await runAz(["cognitiveservices", "usage", "list", "-l", region, "--output", "json"]);
    const raw = JSON.parse(out) as { name?: { value?: string }; currentValue?: number; limit?: number }[];
    return raw.map((u) => ({ name: u.name?.value ?? "", currentValue: u.currentValue ?? 0, limit: u.limit ?? 0 }));
  } catch {
    return [];
  }
}

/** Extract the model id from an Azure usage name `Provider.Tier.<model>` (the model may contain dots). */
export function usageModelName(usageName: string): string {
  return usageName.split(".").slice(2).join(".");
}

// gpt-5*/o-series are OpenAI's reasoning families; the grok-4 family is xAI's.
// Both burn reasoning tokens and both 429 at low deployment capacity, so both
// must match. `grok-3` is excluded deliberately — it predates the reasoning
// split. Note this matches the literal `grok-4`, so a future `grok-5` would
// need adding here rather than being picked up automatically.
const REASONING_USAGE_RE = /gpt-5|o[1-9]|grok-4/i;

/** True when a model deployment name is in the reasoning family (gpt-5* / o-series / the grok-4 family). */
export function isReasoningModel(modelDeployment: string): boolean {
  return REASONING_USAGE_RE.test(modelDeployment);
}

/**
 * Verdict for a model deployment name against a region's quota usages.
 *  - "ok":        ≥1 matching usage entry has limit > 0
 *  - "no_quota":  matching entries exist but ALL have limit 0 (the gpt-5.5 trap)
 *  - "unknown":   no matching usage entry — caller should warn, not block
 * `quotad` lists DEDUPED reasoning-family model names with quota (for the
 * remediation hint) — one model can appear under several SKU tiers
 * (GlobalStandard / DataZoneStandard / Batch), so dedup to the model name.
 */
export function modelQuotaVerdict(
  usages: ModelQuota[],
  modelDeployment: string,
): { verdict: "ok" | "no_quota" | "unknown"; quotad: string[] } {
  const token = modelDeployment.trim().toLowerCase();
  const matches = usages.filter((u) => usageModelName(u.name).toLowerCase() === token);
  const quotad = [
    ...new Set(usages.filter((u) => u.limit > 0 && REASONING_USAGE_RE.test(u.name)).map((u) => usageModelName(u.name))),
  ];
  if (matches.length === 0) return { verdict: "unknown", quotad };
  if (matches.some((m) => m.limit > 0)) return { verdict: "ok", quotad };
  return { verdict: "no_quota", quotad };
}
